import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { folderBreadcrumb } from "@/components/FolderSidebar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import FieldStartedInbox from "@/components/FieldStartedInbox";
import {
  Plus, FileText, Megaphone, Trash2, Eye, ChevronRight, Search, X, Building2, Zap, Bell,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { fi, enGB } from "date-fns/locale";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import MoveToFolderDialog from "@/components/MoveToFolderDialog";

const STATUS_BADGE_CLS = {
  draft: "badge-slate",
  in_progress: "badge-amber",
  completed: "badge-emerald",
  sent: "badge-blue",
};

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const selectedFolder = params.get("folder") || "ALL";
  const { t, isEn } = useT();
  const { user } = useAuth();
  const dateLocale = isEn ? enGB : fi;

  const [reports, setReports] = useState([]);
  const [notices, setNotices] = useState([]);
  const [quickReports, setQuickReports] = useState([]);
  const [folders, setFolders] = useState([]);
  const [defaultPropertyId, setDefaultPropertyId] = useState(null);
  const [tab, setTab] = useState("raportit");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [moveDialog, setMoveDialog] = useState(null); // {kind, itemId, currentFolderId}
  // Massavalinta: setti valittujen report_id:istä. Kun size>0, alalaidassa
  // näkyy massatoimintopalkki (Siirrä / Tyhjennä valinta).
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = async () => {
    try {
      const [r, n, q, f, p] = await Promise.all([
        api.get("/reports"),
        api.get("/notices"),
        api.get("/quick-reports"),
        api.get("/folders"),
        api.get("/properties").catch(() => ({ data: { properties: [] } })),
      ]);
      setReports(r.data);
      setNotices(n.data);
      setQuickReports(q.data.quick_reports || []);
      setFolders(f.data.folders || []);
      const defaultProp = (p.data.properties || []).find((pr) => pr.is_default);
      setDefaultPropertyId(defaultProp?.property_id || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener("veraso-folders-refresh", handler);
    return () => window.removeEventListener("veraso-folders-refresh", handler);
  }, []);

  const removeNotice = async (id) => {
    if (!confirm(t("toast.deleteNoticeConfirm"))) return;
    try {
      await api.delete(`/notices/${id}`);
      setNotices((arr) => arr.filter((x) => x.notice_id !== id));
      toast.success(t("toast.noticeDeleted"));
    } catch { toast.error(t("toast.deleteFailed")); }
  };

  const removeReport = async (id) => {
    if (!confirm(t("toast.deleteReportConfirm"))) return;
    try {
      await api.delete(`/reports/${id}`);
      setReports((arr) => arr.filter((x) => x.report_id !== id));
      toast.success(t("toast.reportDeleted"));
    } catch { toast.error(t("toast.deleteFailed")); }
  };

  const descendantIds = useMemo(() => {
    if (!selectedFolder || selectedFolder === "ALL" || selectedFolder === "UNFILED") return null;
    const byParent = {};
    for (const f of folders) {
      const p = f.parent_id || "ROOT";
      (byParent[p] ||= []).push(f.folder_id);
    }
    const out = new Set([selectedFolder]);
    const stack = [selectedFolder];
    while (stack.length) {
      const cur = stack.pop();
      for (const c of byParent[cur] || []) { out.add(c); stack.push(c); }
    }
    return out;
  }, [selectedFolder, folders]);

  const filterByFolder = (item) => {
    if (selectedFolder === "ALL" || !selectedFolder) return true;
    if (selectedFolder === "UNFILED") return !item.folder_id;
    return descendantIds?.has(item.folder_id);
  };

  // Dashboard shows only reports that are NOT assigned to a specific property.
  // Once a report is moved to a property (Kohde), it lives in that property's
  // detail view, not on the dashboard "etusivu" landing area.
  const isOnDashboard = (item) => {
    const pid = item.property_id;
    if (!pid) return true;
    if (defaultPropertyId && pid === defaultPropertyId) return true;
    return false;
  };

  const filteredReports = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter((r) => {
      if (!filterByFolder(r)) return false;
      if (!isOnDashboard(r)) return false;
      if (!q) return true;
      return [r.title, r.work_type, r.customer_name, r.site_address, r.executor_company]
        .some((s) => (s || "").toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports, query, selectedFolder, descendantIds, defaultPropertyId]);

  const filteredNotices = useMemo(() => {
    const q = query.trim().toLowerCase();
    return notices.filter((n) => {
      if (!filterByFolder(n)) return false;
      if (!q) return true;
      return [n.title, n.site_name, n.site_address, n.customer_name]
        .some((s) => (s || "").toLowerCase().includes(q));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notices, query, selectedFolder, descendantIds]);

  const breadcrumb = folderBreadcrumb(folders, selectedFolder);

  // Etusivu näyttää VAIN viimeisen 7 päivän aikana luodut raportit — kaikki
  // vanhemmat löytyvät "Kohteet → Yleiset raportit" -näkymästä, jonne ne
  // auto-tallentuvat ensurella (ensure_default_property). Näin työnjohdolla
  // ei ole paisuvaa pääsivua, mutta mikään ei katoa.
  const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
  const recentCutoff = Date.now() - RECENT_WINDOW_MS;
  const isRecent = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return !isNaN(t) && t >= recentCutoff;
  };
  const recentReports = useMemo(
    () => filteredReports.filter((r) => isRecent(r.created_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredReports]
  );
  const recentQuickReports = useMemo(
    () => quickReports.filter((q) => isRecent(q.created_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quickReports]
  );
  const recentNotices = useMemo(
    () => filteredNotices.filter((n) => isRecent(n.created_at)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredNotices]
  );
  const hasOlderReports = filteredReports.length > recentReports.length;

  // Build tabs: hide notices tab entirely for English users
  const tabsDef = [
    {
      k: "raportit",
      l: hasOlderReports
        ? `Viimeiset 7 päivää (${recentReports.length}/${filteredReports.length})`
        : `${t("dash.tabReports")} (${filteredReports.length})`,
      i: FileText,
    },
    // Pikaraportit-välilehti piilotettu — ohjaamme käyttäjät pitkään raportin
    // luontiin. Data säilyy backendissä, mutta UI ei näytä välilehteä eikä listaa.
    ...(isEn ? [] : [{
      k: "tiedotteet",
      l: filteredNotices.length > recentNotices.length
        ? `${t("dash.tabRecentNotices")} (${recentNotices.length}/${filteredNotices.length})`
        : `${t("dash.tabNotices")} (${filteredNotices.length})`,
      i: Megaphone,
    }]),
  ];

  // If user is EN and previously selected the (now hidden) notices tab, fall back
  if (isEn && tab === "tiedotteet") {
    setTab("raportit");
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 md:py-8">
        {/* Centered brand wordmark — shown only on the dashboard etusivu */}
        <div className="flex justify-center md:pr-32 lg:pr-40 mb-6 md:mb-8" data-testid="dashboard-brand-wordmark">
          <Link to="/app" className="inline-flex items-baseline gap-1 font-display font-bold tracking-tight text-2xl md:text-3xl">
            <span className="text-blue-900">Veraso</span>
            <span className="text-emerald-600">Reports</span>
          </Link>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">{t("dash.eyebrow")}</div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">{t("dash.title")}</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!isEn && (
              <Link to="/app/asukastiedote/uusi" className="btn-ghost" data-testid="dashboard-create-notice-button">
                <Megaphone className="h-4 w-4" /> {t("dash.createNotice")}
              </Link>
            )}
            <Link
              to={`/app/uusi${selectedFolder && selectedFolder !== "ALL" && selectedFolder !== "UNFILED" ? `?folder=${selectedFolder}` : ""}`}
              className="btn-primary"
              data-testid="dashboard-create-report-button"
            >
              <Plus className="h-4 w-4" /> {t("dash.createReport")}
            </Link>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 flex-wrap" data-testid="dashboard-breadcrumb">
          {breadcrumb.map((b, i) => {
            const label = b.id === "ALL" ? t("dash.crumbAll") : b.id === "UNFILED" ? t("dash.crumbUnfiled") : b.name;
            return (
              <span key={b.id} className="inline-flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-slate-300" />}
                <span className={i === breadcrumb.length - 1 ? "text-slate-900 font-semibold" : ""}>{label}</span>
              </span>
            );
          })}
          {selectedFolder && selectedFolder !== "ALL" && (
            <Link
              to="/app"
              className="ml-2 text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5"
              data-testid="breadcrumb-clear"
            >
              <X className="h-3 w-3" /> {t("dash.clear")}
            </Link>
          )}
        </div>

        <FieldStartedInbox reports={reports} currentUserId={user?.user_id} onOpen={(r) => navigate(`/app/raportti/${r.report_id}`)} />

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("dash.searchPlaceholder")}
              className="input-base pl-10"
              data-testid="dashboard-search"
            />
          </div>
          {query && (
            <button onClick={() => setQuery("")} className="btn-ghost text-sm" data-testid="dashboard-clear-filters">
              <X className="h-4 w-4" /> {t("dash.clear")}
            </button>
          )}
        </div>

        <div className="card-soft overflow-hidden">
          <div className="flex border-b border-slate-100">
            {tabsDef.map((tt) => (
              <button
                key={tt.k}
                onClick={() => setTab(tt.k)}
                className={`px-5 py-4 font-semibold text-sm border-b-2 transition-colors inline-flex items-center gap-2 ${
                  tab === tt.k ? "border-emerald-500 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
                data-testid={`dashboard-tab-${tt.k}`}
              >
                <tt.i className="h-4 w-4" />
                {tt.l}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">{t("dash.loading")}</div>
          ) : tab === "raportit" ? (
            recentReports.length === 0 ? (
              hasOlderReports ? (
                <div className="p-10 text-center" data-testid="dashboard-no-recent-reports">
                  <div className="h-14 w-14 rounded-2xl bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-7 w-7" />
                  </div>
                  <div className="font-display text-lg font-semibold text-slate-900">Ei raportteja viimeiseltä 7 päivältä</div>
                  <div className="text-sm text-slate-500 mt-1">
                    Vanhemmat raportit ({filteredReports.length}) löytyvät Kohteet-näkymästä.
                  </div>
                  <Link to="/app/kohteet" className="btn-ghost mt-5 inline-flex" data-testid="dashboard-no-recent-see-all">
                    Avaa Kohteet →
                  </Link>
                  <div className="mt-2">
                    <Link to="/app/uusi" className="btn-primary inline-flex" data-testid="dashboard-no-recent-create">
                      <Plus className="h-4 w-4" /> {t("dash.emptyCreateReport")}
                    </Link>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={FileText}
                  title={query ? t("dash.noMatches") : (selectedFolder === "UNFILED" ? t("dash.emptyReportsUnfiled") : t("dash.emptyReports"))}
                  subtitle={query ? t("dash.tryDifferent") : t("dash.emptyReportsSub")}
                  cta={!query ? <Link to="/app/uusi" className="btn-primary mt-5 inline-flex" data-testid="empty-create-report"><Plus className="h-4 w-4" /> {t("dash.emptyCreateReport")}</Link> : null}
                  testId="dashboard-empty-reports"
                />
              )
            ) : (
              <div className="divide-y divide-slate-100">
                {recentReports.map((r) => {
                  const statusKey = STATUS_BADGE_CLS[r.status] ? r.status : "draft";
                  const stCls = STATUS_BADGE_CLS[statusKey];
                  const stLabel = t(`status.${statusKey}`);
                  const when = r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true, locale: dateLocale }) : "";
                  const folder = r.folder_id ? folders.find((f) => f.folder_id === r.folder_id) : null;
                  const checked = selectedIds.has(r.report_id);
                  const toggle = (e) => {
                    e.stopPropagation();
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(r.report_id)) next.delete(r.report_id);
                      else next.add(r.report_id);
                      return next;
                    });
                  };
                  return (
                    <div key={r.report_id} className={`flex items-center gap-3 md:gap-4 p-4 md:p-5 transition-colors ${checked ? "bg-emerald-50/60" : "hover:bg-slate-50"}`} data-testid={`report-row-${r.report_id}`}>
                      <label onClick={(e) => e.stopPropagation()} className="flex items-center flex-shrink-0 cursor-pointer p-1 -m-1" data-testid={`report-select-${r.report_id}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={toggle}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                        />
                      </label>
                      <Link to={`/app/raportti/${r.report_id}`} className="flex-1 flex items-center gap-3 md:gap-4 min-w-0">
                        <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display font-semibold text-slate-900 truncate">{r.title}</div>
                          <div className="text-sm text-slate-500 truncate">
                            {r.work_type} · {r.unit_count} {r.unit_count !== 1 ? t("dash.unitMany") : t("dash.unitOne")}
                            {r.customer_name ? ` · ${r.customer_name}` : ""}
                            {folder ? ` · ${folder.name}` : ""}
                          </div>
                        </div>
                        <div className="hidden sm:block text-xs text-slate-500 whitespace-nowrap">{when}</div>
                        <span className={`badge ${stCls}`}>{stLabel}</span>
                      </Link>
                      <button
                        onClick={() => setMoveDialog({ kind: "report", itemId: r.report_id, currentPropertyId: r.property_id })}
                        className="p-2 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600"
                        data-testid={`report-move-${r.report_id}`}
                        title={t("dash.moveToProperty")}
                      >
                        <Building2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => removeReport(r.report_id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600"
                        data-testid={`report-delete-${r.report_id}`}
                        title={t("dash.deleteTitle")}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
                {hasOlderReports && (
                  <Link
                    to="/app/kohteet"
                    className="flex items-center justify-center gap-2 p-4 text-sm font-semibold text-emerald-700 bg-emerald-50/40 hover:bg-emerald-50 transition-colors border-t border-emerald-100"
                    data-testid="dashboard-see-all-reports"
                  >
                    Näytä kaikki raportit ({filteredReports.length}) Kohteissa →
                  </Link>
                )}
              </div>
            )
          ) : tab === "pika" ? (
            quickReports.length === 0 ? (
              <EmptyState
                icon={Zap}
                title={t("dash.emptyQuick")}
                subtitle={t("dash.emptyQuickSub")}
                cta={<Link to="/app/pikaraportti/uusi" className="btn-primary mt-5 inline-flex" data-testid="empty-create-quick"><Zap className="h-4 w-4" /> {t("dash.createQuick")}</Link>}
                testId="dashboard-empty-quick"
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {recentQuickReports.map((q) => {
                  const when = q.created_at ? formatDistanceToNow(new Date(q.created_at), { addSuffix: true, locale: dateLocale }) : "";
                  return (
                    <div key={q.quick_id} className="flex items-center gap-3 md:gap-4 p-4 md:p-5 hover:bg-slate-50 transition-colors" data-testid={`quick-row-${q.quick_id}`}>
                      <div className="h-11 w-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center flex-shrink-0">
                        <Zap className="h-5 w-5" />
                      </div>
                      <Link to={`/app/pikaraportti/${q.quick_id}`} className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display font-semibold text-slate-900 truncate">{q.title}</div>
                          {q.findings_notify_office && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                              title={t("dash.toOfficeTitle")}
                              data-testid={`quick-row-notify-${q.quick_id}`}
                            >
                              <Bell className="h-3 w-3" /> {t("dash.toOffice")}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-slate-500 truncate">
                          {q.customer_name && <>{q.customer_name} · </>}
                          {q.photo_count > 0 ? `${q.photo_count} ${t("dash.photosCount")}` : t("dash.noPhotos")}
                        </div>
                      </Link>
                      <div className="hidden sm:block text-xs text-slate-500">{when}</div>
                    </div>
                  );
                })}
              </div>
            )
          ) : filteredNotices.length === 0 ? (
            <EmptyState
              icon={Megaphone}
              title={query ? t("dash.noMatches") : t("dash.emptyNotices")}
              subtitle={query ? t("dash.tryDifferent") : t("dash.emptyNoticesSub")}
              cta={!query ? <Link to="/app/asukastiedote/uusi" className="btn-primary mt-5 inline-flex" data-testid="empty-create-notice"><Megaphone className="h-4 w-4" /> {t("dash.createNotice")}</Link> : null}
              testId="dashboard-empty-notices"
            />
          ) : (
            <div className="divide-y divide-slate-100">
              {recentNotices.map((n) => {
                const when = n.created_at ? formatDistanceToNow(new Date(n.created_at), { addSuffix: true, locale: dateLocale }) : "";
                const folder = n.folder_id ? folders.find((f) => f.folder_id === n.folder_id) : null;
                return (
                  <div key={n.notice_id} className="flex items-center gap-3 md:gap-4 p-4 md:p-5 hover:bg-slate-50 transition-colors" data-testid={`notice-row-${n.notice_id}`}>
                    <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                      <Megaphone className="h-5 w-5" />
                    </div>
                    <Link to={`/app/asukastiedote/${n.notice_id}`} className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-slate-900 truncate">{n.title}</div>
                      <div className="text-sm text-slate-500 truncate">
                        {n.site_name && <>{n.site_name} · </>}
                        {n.site_address}
                        {n.date && <> · {new Date(n.date).toLocaleDateString(isEn ? "en-GB" : "fi-FI")}</>}
                        {folder ? ` · ${folder.name}` : ""}
                      </div>
                    </Link>
                    <div className="hidden sm:block text-xs text-slate-500">{when}</div>
                    <Link to={`/app/asukastiedote/${n.notice_id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" data-testid={`notice-view-${n.notice_id}`}>
                      <Eye className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => setMoveDialog({ kind: "notice", itemId: n.notice_id, currentPropertyId: n.folder_id })}
                      className="p-2 rounded-lg hover:bg-emerald-50 text-slate-400 hover:text-emerald-600"
                      data-testid={`notice-move-${n.notice_id}`}
                      title={t("dash.moveToProperty")}
                    >
                      <Building2 className="h-4 w-4" />
                    </button>
                    <button onClick={() => removeNotice(n.notice_id)} className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`notice-delete-${n.notice_id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {moveDialog && (
        <MoveToFolderDialog
          kind={moveDialog.kind}
          itemId={moveDialog.itemId}
          itemIds={moveDialog.itemIds}
          currentPropertyId={moveDialog.currentPropertyId}
          onClose={() => setMoveDialog(null)}
          onMoved={(newId, movedCount) => {
            if (moveDialog.kind === "bulk-reports") {
              const ids = new Set(moveDialog.itemIds || []);
              setReports((arr) => arr.map((r) => ids.has(r.report_id) ? { ...r, property_id: newId } : r));
              setSelectedIds(new Set());
              if (typeof movedCount === "number") {
                // Toast already fired by MoveToFolderDialog
              }
            } else if (moveDialog.kind === "report") {
              setReports((arr) => arr.map((r) => r.report_id === moveDialog.itemId ? { ...r, property_id: newId } : r));
            } else {
              setNotices((arr) => arr.map((n) => n.notice_id === moveDialog.itemId ? { ...n, folder_id: newId } : n));
            }
          }}
        />
      )}

      <BulkActionBar
        count={selectedIds.size}
        onClear={() => setSelectedIds(new Set())}
        onMove={() => setMoveDialog({ kind: "bulk-reports", itemIds: Array.from(selectedIds) })}
      />
    </div>
  );
}

function EmptyState({ icon: Icon, title, subtitle, cta, testId }) {
  return (
    <div className="p-12 md:p-16 text-center" data-testid={testId}>
      <div className="h-14 w-14 rounded-2xl bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-4">
        <Icon className="h-7 w-7" />
      </div>
      <div className="font-display text-lg font-semibold text-slate-900">{title}</div>
      <p className="text-slate-600 mt-2 max-w-md mx-auto">{subtitle}</p>
      {cta}
    </div>
  );
}

/**
 * BulkActionBar — kelluva alapalkki joka näkyy kun ≥1 raportti on valittu.
 * Käyttäjä siirtää valitut työt kerralla oikeaan kohteeseen tai tyhjentää
 * valinnan. Poistuu itsestään kun valintoja ei enää ole.
 */
function BulkActionBar({ count, onMove, onClear }) {
  if (count === 0) return null;
  return (
    <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center pointer-events-none" data-testid="bulk-action-bar">
      <div className="pointer-events-auto max-w-lg w-[calc(100%-2rem)] mx-4 rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-black/10 px-4 py-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
          <span className="text-sm font-bold tabular-nums">{count}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight">{count === 1 ? "1 työ valittu" : `${count} työtä valittu`}</div>
          <div className="text-[11px] text-white/60 mt-0.5">Siirrä oikeaan kohteeseen yhdellä toiminnolla.</div>
        </div>
        <button
          onClick={onClear}
          className="p-2 rounded-lg hover:bg-white/10 text-white/80"
          title="Tyhjennä valinta"
          data-testid="bulk-clear"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={onMove}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold whitespace-nowrap"
          data-testid="bulk-move"
        >
          <Building2 className="h-4 w-4" /> Siirrä
        </button>
      </div>
    </div>
  );
}

